import { Injectable } from '@angular/core';
import Big, { RoundingMode } from 'big.js';
import dayjs from 'dayjs';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { OvertimeTotals } from '../models/overtime-totals.model';
import { VacationWeeks } from '../models/vacation-weeks.model';
import { FormulaHelperService } from './formula-helper.service';
import { AndOr, HoursFrequency, MarginCalculatorBillingService } from './margin-calculator-pay-billing.service';

@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorFormulaService {
    constructor(
        private readonly _marginBillingService: MarginCalculatorBillingService
    ) { }

    calculateShiftRate(otPayRate: number, blendedPayRate: number, shiftLength: number): number {
        const length = Number(shiftLength);

        if (length === 8) {
            return this.roundNumber((otPayRate - this.roundNumber(blendedPayRate * 1.5)) * length);
        } else {
            return this.roundNumber(this.roundNumber(otPayRate - blendedPayRate) * length);
        }
    }

    calculateHousewaresTotal(housewaresTotal: number, numOfWeeks: number, prorateMultiplier: number): number {
        return +(housewaresTotal * (12 / 52) * numOfWeeks).toFixed(2) * prorateMultiplier;
    }

    calculateFurnitureContractCost(furnitureCost: number, numOfWeeks: number, prorateMultiplier: number): number {
        return +(furnitureCost * (numOfWeeks / 4.333)).toFixed(2) * prorateMultiplier;
    }

    calculateUtilitiesContractCost(utilitiesCost: number, numOfWeeks: number, prorateMultiplier: number): number {
        return +(utilitiesCost * (numOfWeeks / 4.333)).toFixed(2) * prorateMultiplier;
    }

    calculateHousewaresContractCost(housewaresTotal: number, numOfWeeks: number, prorateMultiplier: number): number {
        return +(housewaresTotal * (12 / 52) * numOfWeeks).toFixed(2) * prorateMultiplier;
    }

    calculateMonthlyRent(monthlyRent: number, weeks: number, prorateMultiplier: number): number {
        return this.roundNumber(monthlyRent * (12 / 52) * weeks) * prorateMultiplier;
    }

    calculateRecruiterHourlyBaseRate(contractCommission: number, totalRegHrs: number, totalOtHrs: number): number {
        return contractCommission / (totalRegHrs + totalOtHrs);
    }

    calculateContractCommission(profit: number, commissionMultiplier: number): number {
        return profit * commissionMultiplier;
    }

    calculateBillableTotal(
        totalRegHrs: number,
        adjustedBillRate: number,
        totalOtHrs: number,
        adjustedOtBillRate: number,
        orientationBillTotal: number
    ): number {
        return totalRegHrs * Number(adjustedBillRate) + totalOtHrs * Number(adjustedOtBillRate) + orientationBillTotal;
    }

    calculateNonStandardBillableTotal(
        primaryHrs: number,
        orientationHrs: number,
        adjustedBillRate: number,
        primaryOtHrs: number,
        adjustedOtBillRate: number,
        altHrs: number,
        altAdjustedBillRate: number,
        altOtHrs: number,
        altAdjustedOtBillRate: number,
        orientationBillTotal: number
    ): number {
        return (
            (primaryHrs - orientationHrs) * adjustedBillRate +
            orientationBillTotal +
            primaryOtHrs * adjustedOtBillRate +
            altHrs * altAdjustedBillRate +
            altOtHrs * altAdjustedOtBillRate
        );
    }

    calculateNonStandardRegularTotal(
        primaryHrs: number,
        orientHours: number,
        adjustedBillRate: number,
        altHrs: number,
        altAdjustedBillRate: number,
        orientationBillTotal: number
    ): number {
        return (primaryHrs - orientHours) * adjustedBillRate + orientationBillTotal + altHrs * altAdjustedBillRate;
    }

    calculateNonStandardOtTotal(
        primaryOtHrs: number,
        adjustedOtBillRate: number,
        altOtHrs: number,
        altAdjustedOtBillRate: number
    ): number {
        return primaryOtHrs * adjustedOtBillRate + altOtHrs * altAdjustedOtBillRate;
    }

    calculateProfit(billableTotal: number, expenseTotal: number): number {
        return billableTotal - expenseTotal;
    }

    calculateAdjustedBillRate(
        extension: boolean,
        regBillRate: number,
        paybackPercent: number,
        extDiscount: number,
        discountPercent: number,
        extDiscountType: boolean
    ): number {
        let adjustedBillRate = regBillRate * (1 - paybackPercent) * (1 - discountPercent);

        if (extension) {
            if (extDiscountType) {
                adjustedBillRate -= extDiscount * 100;
            } else {
                adjustedBillRate *= 1 - extDiscount;
            }
        }

        return this.roundNumber(adjustedBillRate);
    }

    calculateAdjustedOtBillRate(
        extension: boolean,
        otBillRate: number,
        paybackPercent: number,
        extDiscount: number,
        discountPercent: number,
        extDiscountType: boolean
    ): number {
        let adjustedOtBillRate = otBillRate * (1 - paybackPercent) * (1 - discountPercent);

        if (extension) {
            if (extDiscountType) {
                extDiscount *= 100;
                adjustedOtBillRate -= extDiscount;
            } else {
                adjustedOtBillRate *= 1 - extDiscount;
            }
        }

        return this.roundNumber(adjustedOtBillRate);
    }

    calculateAdjustedOrientationRate(
        extension: boolean,
        orientHours: number,
        orientRate: number,
        paybackPercent: number,
        extDiscount: number,
        discountPercent: number,
        extDiscountType: boolean
    ): number {
        let adjustedRate = orientRate * (1 - paybackPercent) * (1 - discountPercent);

        if (extension) {
            if (extDiscountType) {
                adjustedRate -= extDiscount * 100;
            } else {
                adjustedRate *= 1 - extDiscount;
            }
        }

        return orientHours * adjustedRate;
    }

    calculateTotalPayHrs(bookingWeeks: number, numOfShifts: number, shiftLength: number): number {
        return bookingWeeks * numOfShifts * shiftLength;
    }

    calculateContractStipend(mealsDailyMax: number, daysInContract: number, houseDailyMax: number): number {
        return mealsDailyMax * daysInContract + houseDailyMax * daysInContract;
    }

    calculatePremiumMargin(premiumBillRate: number, otWage: number, multiplier?: number): number {
        return (premiumBillRate - otWage * (1 + multiplier)) / premiumBillRate;
    }

    calculateOTRate(facilityState: string, basePayRate: number, otPayRate: number): number {
        let otRate = 0;
        if (facilityState === 'AK' || facilityState === 'CA' || facilityState === 'VI') {
            if (otPayRate > this.calculateMinPremiumPayRate(basePayRate)) {
                otRate = this.calculateMinPremiumPayRate(basePayRate);
            } else {
                otRate = otPayRate;
            }
        } else {
            otRate = otPayRate;
        }
        return otRate;
    }

    calculateBlendedPayRate(facilityState: string, basePayRate: number, otPayRate: number, shiftHours: number): number {
        const otRate = this.calculateOTRate(facilityState, basePayRate, otPayRate);

        if (shiftHours > 8) {
            return (8 * basePayRate + (shiftHours - 8) * otRate) / shiftHours;
        } else {
            return basePayRate;
        }
    }

    calculateAdditionalCosts(
        ayaBooster: number,
        internalCosts: number,
        internalStateCosts: number,
        labWorkCosts: number
    ): number {
        return internalCosts + internalStateCosts + labWorkCosts - ayaBooster;
    }

    calculateHolidayPayRate(payRate: number): number {
        return payRate * 1.5;
    }

    calculatePremiumBillRate(
        billRate: number,
        otHrs: number,
        otRate: number,
        numOfShifts: number,
        shiftHours: number
    ): number {
        return (billRate * otHrs + otRate * numOfShifts) / shiftHours;
    }

    calculateOnCallPayRate(onCallBillRate: number): number {
        return onCallBillRate / 2;
    }

    calculateChargePayRate(chargeBillRate: number): number {
        return chargeBillRate / 2;
    }

    calculateAdjustedRate(rate: number, contractFees: number, extFees: number, earlyPmtDiscount: number): number {
        return new Big(rate)
            .times(1 - contractFees)
            .times(1 - extFees)
            .times(1 - earlyPmtDiscount)
            .round(2, Big.roundHalfUp)
            .toNumber();
    }

    calculateRateByMultiplier(
        multiplier: string,
        multiplierAmount: number,
        baseRate: number,
        rm?: RoundingMode
    ): number {
        let rate = 0;
        if (multiplierAmount === null) {
            return baseRate;
        }
        switch (multiplier) {
            case '%':
                rate = this._multiplyOtRate(multiplierAmount, baseRate, rm);
                break;
            case '$':
                rate = this._addOtRate(multiplierAmount, baseRate, rm);
                break;
            default:
                rate = baseRate;
                break;
        }

        return rate;
    }

    calculateWeeklyMeals(weeklyStipend: number, housing: boolean): number {
        if (!housing) {
            return weeklyStipend * 0.4;
        } else {
            return weeklyStipend;
        }
    }

    calculateHousingDailyAmount(weeklyStipend: number, dailyMealsStipend: number): number {
        return weeklyStipend / 7 - dailyMealsStipend;
    }

    calculateMealsDailyAmount(weeklyAmount: number, needToMultiplyFirst: boolean): number {
        return needToMultiplyFirst ? (weeklyAmount * 0.4) / 7 : weeklyAmount / 7;
    }

    calculateStipendsPerContract(mealsStipend: number, housingStipend: number, bookingWeeks: number): number {
        return mealsStipend * bookingWeeks + housingStipend * bookingWeeks;
    }

    calculateShiftAmounts(numOfShifts: number, dailyStipend: number): number {
        return (7 / numOfShifts) * dailyStipend;
    }

    calculateEntireContractGross(grossPay: number, housingGross: number, mealsGross: number): number {
        return grossPay + housingGross + mealsGross;
    }

    calculateEntireContractGrossWeekly(grossPay: number, bookingWeeks: number): number {
        return grossPay / bookingWeeks;
    }

    calculateEntireContractGrossHourly(grossPay: number, stipend: number, totalHoursPerWeek: number): number {
        return (grossPay + stipend) / totalHoursPerWeek;
    }

    calculateWeeklyShiftTotal(numOfShifts: number, dailyHousing: number, dailyMeals: number): number {
        return (7 / numOfShifts) * dailyHousing + (7 / numOfShifts) * dailyMeals;
    }

    calculateWeeklyPay(
        facilityState: string,
        basePayRate: number,
        otPayRate: number,
        regPayHrs: number,
        otPayHrs: number,
        taxRate: number,
        weeklyStipend: number
    ): number {
        const otRate = this.calculateOTRate(facilityState, basePayRate, otPayRate);
        return this.roundNumber((basePayRate * regPayHrs + otRate * otPayHrs) * (1 - taxRate) + weeklyStipend);
    }

    calculateRotatingEstWeekly(
        firstShiftRegHrs: number,
        secondShiftRegHrs: number,
        firstShiftOtHrs: number,
        secondShiftOtHrs: number,
        regPay: number,
        otPay: number,
        taxRate: number,
        weeklyStipend: number
    ): number {
        const shiftOneRegPay = this.calculateWeeklyRegPay(firstShiftRegHrs, regPay);
        const shiftOneOtPay = this.calculateWeeklyOtPay(firstShiftOtHrs, otPay);
        const shiftTwoRegPay = this.calculateWeeklyRegPay(secondShiftRegHrs, regPay);
        const shiftTwoOtPay = this.calculateWeeklyOtPay(secondShiftOtHrs, otPay);

        return (shiftOneRegPay + shiftTwoRegPay + shiftOneOtPay + shiftTwoOtPay) * (1 - taxRate) + weeklyStipend;
    }

    calculateWeeklyRegPay(totalRegHoursPerWeek: number, basePayRate: number): number {
        return totalRegHoursPerWeek * basePayRate;
    }

    calculateWeeklyOtPay(totalOtHoursPerWeek: number, otPayRate: number): number {
        return totalOtHoursPerWeek * otPayRate;
    }

    calculateEstWeeklyStipend(mealsStipend: number, housingStipend: number): number {
        return mealsStipend + housingStipend;
    }

    calculateOverTimePayHrs(totalHoursPerWeek: number): number {
        if (totalHoursPerWeek > 40) {
            return totalHoursPerWeek - 40;
        } else {
            return 0;
        }
    }

    calculateRegPayHrs(totalHoursPerWeek: number): number {
        if (totalHoursPerWeek > 40) {
            return totalHoursPerWeek - this.calculateOverTimePayHrs(totalHoursPerWeek);
        } else {
            return totalHoursPerWeek;
        }
    }

    private _multiplyOtRate(multiplierAmount: number, baseBillRate: number, rm?: RoundingMode): number {
        return new Big(baseBillRate).times(multiplierAmount).round(2, rm).toNumber();
    }

    private _addOtRate(multiplierAmount: number, baseBillRate: number, rm?: RoundingMode): number {
        return new Big(baseBillRate).add(multiplierAmount).round(2, rm).toNumber();
    }

    calculateTotalWeeks(from: Date, to: Date): number {
        if (isEmpty(from) || isEmpty(to) || to < from) {
            return -1;
        }

        let currentDate = new Date(from);
        let weekCount = 0;

        while (currentDate < to) {
            const currentDay = currentDate.getDay();
            //0,    1,      2,  3,  4,      5,  6
            // sun, mon, tues, wed, thurs, Fri, sat

            let difference = 6 - currentDay;
            const dateOfEndOfWeek = new Date(currentDate.setDate(currentDate.getDate() + difference));

            if (dateOfEndOfWeek > to) {
                difference = to.getDay() - currentDay;
            }

            if (difference > 2) {
                weekCount++;
            }

            if (dateOfEndOfWeek < to) {
                currentDate = new Date(dateOfEndOfWeek.setDate(dateOfEndOfWeek.getDate() + 1));
            } else {
                currentDate = to;
            }
        }

        return weekCount;
    }

    // This method will be used when we move to the new week calculation logic.
    calculateWeeksNewWay(from: Date, to: Date): number {
        if (isEmpty(from) || isEmpty(to) || to < from) {
            return -1;
        }

        let currentDate = new Date(from);
        let weekCount = 0;

        while (currentDate < to) {
            const currentDay = currentDate.getDay();
            //0,    1,      2,  3,  4,      5,  6
            // sun, mon, tues, wed, thurs, Fri, sat

            let difference = 6 - currentDay;
            const dateOfEndOfWeek = new Date(currentDate);
            dateOfEndOfWeek.setDate(dateOfEndOfWeek.getDate() + difference);

            if (dateOfEndOfWeek > to) {
                difference = to.getDay() - currentDay;
            }

            if (difference > 2) {
                weekCount++;
            }

            if (dateOfEndOfWeek < to) {
                currentDate = new Date(dateOfEndOfWeek.setDate(dateOfEndOfWeek.getDate() + 1));
            } else {
                currentDate = to;
            }
        }

        return weekCount;
    }

    calculateWeeks(from: Date, to: Date): number {
        const start = dayjs(from);
        const end = dayjs(to);

        const days = end.diff(start, 'day');

        let weekCount = Math.floor(days / 7);

        if (days % 7 > 2) {
            weekCount++;
        }

        return weekCount;
    }

    calculateToDate(from: Date, weeks: number): Date {
        if (weeks < 1) {
            return null;
        }

        const currentDay = from.getDay();
        const currentDate = new Date(from);
        let totalDays = weeks * 7 - 1;

        if (currentDay < 4) {
            totalDays = totalDays - 1;
        }

        const newDate = currentDate.setDate(currentDate.getDate() + totalDays);
        return new Date(newDate);
    }

    calculateFromDate(to: Date, weeks: number): Date {
        if (weeks < 1) {
            return null;
        }

        const currentDate = new Date(to);
        const totalDays = weeks * 7;

        const newDate = currentDate.setDate(currentDate.getDate() - totalDays + 1);
        return new Date(newDate);
    }

    calculateVacationWeeksForARange(from: Date, to: Date, dates: Date[]): VacationWeeks {
        if (from === null || to === null || to < from || dates.length === 0) {
            return new VacationWeeks();
        }

        let weekCount = 0;
        // Construct the date so timezone information is removed or 'getTime()' will be incorrect
        // See here: https://medium.com/@sampathsl/how-to-convert-javascript-date-without-time-stamp-6dcc2aeb2190
        let currentDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
        let oddVacationWeekCounter = 0;
        let evenVacationWeekCounter = 0;

        while (currentDate < to) {
            const currentDay = currentDate.getDay();

            let difference = 6 - currentDay;
            const dateOfEndOfWeek = new Date(currentDate);
            dateOfEndOfWeek.setDate(dateOfEndOfWeek.getDate() + difference);

            if (dateOfEndOfWeek > to) {
                difference = to.getDay() - currentDay;
            }
            weekCount++;
            if (difference > 2) {
                let vacationCounter = 0;

                let newFrom = new Date(currentDate);
                const newTo = new Date(currentDate);
                newTo.setDate(newTo.getDate() + difference);

                const newDayDifference = Math.abs(newTo.getDay() - newFrom.getDay());
                if (newDayDifference > 3) {
                    while (newFrom <= newTo) {
                        if (this._isInArray(dates, newFrom)) {
                            vacationCounter++;
                        }
                        if (vacationCounter > 4) {
                            if (weekCount % 2 === 0) {
                                evenVacationWeekCounter++;
                            } else {
                                oddVacationWeekCounter++;
                            }
                            vacationCounter = 0;
                            newFrom = new Date(newTo.getTime());
                        }
                        newFrom.setDate(newFrom.getDate() + 1);
                    }
                }
            }

            if (dateOfEndOfWeek < to) {
                currentDate = new Date(dateOfEndOfWeek.setDate(dateOfEndOfWeek.getDate() + 1));
            } else {
                currentDate = to;
            }
        }

        return new VacationWeeks(
            oddVacationWeekCounter,
            evenVacationWeekCounter,
            oddVacationWeekCounter + evenVacationWeekCounter
        );
    }

    private _isInArray(dates: Date[], value: Date): Date {
        return dates.find((item) => {
            return item.getTime() === value.getTime();
        });
    }

    calculateStandardBookingWeeks(from: Date, to: Date, dates: Date[]): number {
        const totalWeeks = this.calculateWeeks(from, to);
        const vacationWeeks = this.calculateVacationWeeksForARange(from, to, dates);

        return totalWeeks - vacationWeeks.totalWeeks;
    }

    calculatePrimaryBookingWeeks(from: Date, to: Date, dates: Date[]): number {
        const totalWeeks = this.calculateWeeks(from, to);

        const totalEvenWeeks = this.getTotalEvenNumbers(totalWeeks);
        const totalOddWeeks = totalWeeks - totalEvenWeeks;

        const vacationWeeks = this.calculateVacationWeeksForARange(from, to, dates);

        return totalOddWeeks - vacationWeeks.oddWeeks;
    }

    calculateAlternatingBookingWeeks(from: Date, to: Date, dates: Date[]): number {
        const totalWeeks = this.calculateWeeks(from, to);
        const totalEvenWeeks = this.getTotalEvenNumbers(totalWeeks);

        const vacationWeeks = this.calculateVacationWeeksForARange(from, to, dates);

        return totalEvenWeeks - vacationWeeks.evenWeeks;
    }

    private getTotalEvenNumbers(value: number): number {
        if (!this.isNumberWhole(value)) {
            return 0;
        }

        return Math.floor(value / 2);
    }

    private isNumberWhole(value: number): boolean {
        return value - Math.floor(value) === 0;
    }

    calculateRegularPayHours(state: string, shiftsPerWeek: number, shiftLength: number, weeks: number): number {
        const totalHours = shiftsPerWeek * shiftLength * weeks;
        const totalOvertimeHours = this.calculateRegularOverTimePayHours(state, shiftsPerWeek, shiftLength, weeks);
        return totalHours - totalOvertimeHours;
    }

    calculateRegularOverTimePayHours(state: string, shiftsPerWeek: number, shiftLength: number, weeks: number): number {
        const isCA = state === 'CA' || state === 'AK' || state === 'VI';
        const dailyOvertimeHours = isCA ? Math.max(shiftLength - 8, 0) * shiftsPerWeek : 0;
        const weeklyOvertimeHours = Math.max(shiftLength * shiftsPerWeek - 40 - dailyOvertimeHours, 0);
        const totalOvertimeHoursEachWeek = dailyOvertimeHours + weeklyOvertimeHours;
        return totalOvertimeHoursEachWeek * weeks;
    }

    calculateNonStandardPayHours(
        state: string,
        shiftsPerWeek: number,
        shiftLength: number,
        altShiftsPerWeek: number,
        altShiftLength: number,
        weeks: number,
        from: Date,
        to: Date,
        vacationDates: Date[] = []      
    ): number {
        // Use same logic for rotating and alternating - MC-1160
        // Originally it just affected billing hours
        // But, when comparing to legacy MC numbers weren't matching        
        const alternateWeeks = this.calculateAlternatingBookingWeeks(from, to, vacationDates);
        const primaryBookingWeeks = weeks - alternateWeeks;        

        const primaryHours = this.calculateRegularPayHours(state, shiftsPerWeek, shiftLength, primaryBookingWeeks);
        const alternateHours = this.calculateRegularPayHours(state, altShiftsPerWeek, altShiftLength, alternateWeeks);

        return primaryHours + alternateHours;
    }

    calculateNonStandardOverTimePayHours(
        state: string,
        shiftsPerWeek: number,
        shiftLength: number,
        altShiftsPerWeek: number,
        altShiftLength: number,
        from: Date,
        to: Date,
        vacationDates: Date[] = []      
    ): number {        
        const primaryBookingWeeks = this.calculatePrimaryBookingWeeks(from, to, vacationDates);
        const alternateBookingWeeks = this.calculateAlternatingBookingWeeks(from, to, vacationDates);        

        const primaryHours = this.calculateRegularOverTimePayHours(
            state,
            shiftsPerWeek,
            shiftLength,
            primaryBookingWeeks
        );
        const alternateHours = this.calculateRegularOverTimePayHours(
            state,
            altShiftsPerWeek,
            altShiftLength,
            alternateBookingWeeks
        );

        return primaryHours + alternateHours;
    }

    // Billing hour logic

    calculateRegularBillingHour(
        shiftLength: number,
        shiftsPerWeek: number,
        weeks: number,
        totalOrientationHours: number,
        otHours: number
    ): number {
        const totalShiftHours = shiftLength * shiftsPerWeek;
        const totalHours = totalShiftHours * weeks - totalOrientationHours;
        return totalHours - otHours;
    }

    calculateOverTimeBillingHour(
        otWeekOrDayFirst: string,
        andOrFirst: string,
        ruleHoursFirst: number,
        otWeekOrDaySecond: string,
        ruleHoursSecond: number,
        shiftLength: number,
        shiftsPerWeek: number,
        weeks: number,
        ruleHourCountFirst: string,
        ruleHourCountSecond: string
    ): number {
        if (!otWeekOrDayFirst) {
            return 0;
        }

        if (andOrFirst === AndOr.EMPTY) {
            return this._calculateSimpleOverTimeBilling(
                otWeekOrDayFirst,
                ruleHoursFirst,
                shiftLength,
                shiftsPerWeek,
                weeks
            );
        } else if (andOrFirst === AndOr.AND || andOrFirst === AndOr.OR) {
            let totalShiftHoursWithDailyRule = 0;
            let totalShiftHoursWithWeeklyRule = 0;
            if (otWeekOrDayFirst === HoursFrequency.DAILY && otWeekOrDaySecond === HoursFrequency.WEEKLY) {
                totalShiftHoursWithDailyRule = ruleHoursFirst;
                totalShiftHoursWithWeeklyRule = ruleHoursSecond;
                return this._calculateOverTimeBillingHourAndOr(
                    shiftsPerWeek,
                    totalShiftHoursWithDailyRule,
                    totalShiftHoursWithWeeklyRule,
                    weeks,
                    shiftLength
                );
            } else if (otWeekOrDayFirst === HoursFrequency.WEEKLY && otWeekOrDaySecond === HoursFrequency.DAILY) {
                totalShiftHoursWithDailyRule = ruleHoursSecond;
                totalShiftHoursWithWeeklyRule = ruleHoursFirst;
                return this._calculateOverTimeBillingHourAndOr(
                    shiftsPerWeek,
                    totalShiftHoursWithDailyRule,
                    totalShiftHoursWithWeeklyRule,
                    weeks,
                    shiftLength
                );
            } else if (
                (otWeekOrDayFirst === HoursFrequency.TWO_WEEK || otWeekOrDaySecond === HoursFrequency.TWO_WEEK) &&
                (otWeekOrDayFirst === HoursFrequency.DAILY || otWeekOrDaySecond === HoursFrequency.DAILY)
            ) {
                totalShiftHoursWithDailyRule =
                    otWeekOrDayFirst === HoursFrequency.DAILY ? ruleHoursFirst : ruleHoursSecond;
                totalShiftHoursWithWeeklyRule =
                    otWeekOrDayFirst === HoursFrequency.TWO_WEEK ? ruleHoursFirst : ruleHoursSecond;
                const count = otWeekOrDayFirst === HoursFrequency.DAILY ? ruleHourCountSecond : ruleHourCountFirst;
                const result = this._calculate2WeekOverTimeBillingHourAndOr(
                    shiftsPerWeek,
                    totalShiftHoursWithDailyRule,
                    totalShiftHoursWithWeeklyRule,
                    weeks,
                    shiftLength,
                    count,
                    shiftsPerWeek,
                    shiftLength
                );
                return result.standardOtTotal + result.altOtTotal;
            }
            return 0;
        }
        return 0;
    }

    private _calculateSimpleOverTimeBilling(
        otWeekOrDayFirst: string,
        ruleHoursFirst: number,
        shiftLength: number,
        shiftsPerWeek: number,
        weeks: number
    ): number {
        const totalShiftHours = shiftLength * shiftsPerWeek;

        if (otWeekOrDayFirst === HoursFrequency.WEEKLY) {
            if (totalShiftHours > ruleHoursFirst) {
                return (totalShiftHours - ruleHoursFirst) * weeks;
            }
        } else if (otWeekOrDayFirst === HoursFrequency.DAILY) {
            if (shiftLength > ruleHoursFirst) {
                return (shiftLength - ruleHoursFirst) * shiftsPerWeek * weeks;
            }
        } else if (otWeekOrDayFirst === HoursFrequency.TWO_WEEK) {
            if (totalShiftHours * 2 > ruleHoursFirst) {
                return (totalShiftHours * 2 - ruleHoursFirst) * Math.floor(weeks / 2);
            }
        }

        return 0;
    }

    private _calculateOverTimeBillingHourAndOr(
        shiftsPerWeek: number,
        dailyRule: number,
        weeklyRule: number,
        weeks: number,
        shiftLength: number
    ): number {
        const dailyOvertimeHours = Math.max(shiftLength - dailyRule, 0) * shiftsPerWeek;
        const weeklyOvertimeHours = Math.max(shiftLength * shiftsPerWeek - weeklyRule - dailyOvertimeHours, 0);
        return (dailyOvertimeHours + weeklyOvertimeHours) * weeks;
    }

    private _calculate2WeekOverTimeBillingHourAndOr(
        shiftsPerWeek: number,
        dailyRule: number,
        twoWeeklyRule: number,
        weeks: number,
        shiftLength: number,
        weeklyHourCount: string,
        altShiftsPerWeek: number,
        altShiftLength: number
    ): OvertimeTotals {
        //weeklyHourCount R = reg, C = total.
        //If weekly hour count is set to R apply weekly rule after regular (non-overtime) hours > weeklyRule
        //If weekly hour count is set to T apply weekly rule after total hours > weeklyRule
        const hoursPerDay =
            shiftLength > dailyRule
                ? dailyRule
                : shiftLength;
        const altHoursPerDay =
            altShiftLength > dailyRule
                ? dailyRule
                : altShiftLength;
        const totalShiftHours = shiftsPerWeek * hoursPerDay;
        const altTotalShiftHours = altShiftsPerWeek * altHoursPerDay;
        let firstWeekOt = 0;
        let secondWeekOt = 0;
        if (totalShiftHours > twoWeeklyRule) {
            const dailyOtDays = Math.floor(twoWeeklyRule / hoursPerDay);
            let remainder = 0;
            if (dailyOtDays < shiftsPerWeek) {
                remainder = twoWeeklyRule % hoursPerDay;
            }
            if (shiftLength > dailyRule) {
                //apply daily rule to first week
                firstWeekOt += (shiftLength - dailyRule) * Math.floor(dailyOtDays);
            }
            if (remainder > 0) {
                firstWeekOt = shiftLength - remainder;
            }
            firstWeekOt += (shiftsPerWeek - Math.ceil(dailyOtDays)) * shiftLength;
            //all hours in second week are ot
            secondWeekOt = altShiftLength * altShiftsPerWeek;
        } else {
            if (shiftLength > dailyRule) {
                //calculate first week daily ot rules
                firstWeekOt = (shiftLength - dailyRule) * shiftsPerWeek;
            }
            if (totalShiftHours + altTotalShiftHours > twoWeeklyRule) {
                //calculate remaining daily ot rules
                const remainingHours = twoWeeklyRule - totalShiftHours;
                const dailyOtDays = remainingHours / altHoursPerDay;
                let remainder = 0;
                if (dailyOtDays < altShiftsPerWeek) {
                    remainder = remainingHours % altHoursPerDay;
                }

                if (altShiftLength > dailyRule) {
                    secondWeekOt += (altShiftLength - dailyRule) * Math.floor(dailyOtDays);
                }
                //on to two week rules
                if (remainder > 0) {
                    secondWeekOt += altShiftLength - remainder;
                }
                secondWeekOt += (altShiftsPerWeek - Math.ceil(dailyOtDays)) * altShiftLength;
            } else {
                //calculate second week daily ot rules
                secondWeekOt = (altShiftLength - dailyRule) * altShiftsPerWeek;
            }
        }
        return {
            standardOtTotal: firstWeekOt * Math.ceil(weeks / 2),
            altOtTotal: secondWeekOt * Math.floor(weeks / 2)
        };
    }

    _calculateTotalMonth(weeks: number): number {
        return Math.floor(weeks / 4);
    }

    calculateDailyHousingAmountFromWeeklyHousingStipend(weeklyHousingStipend: number): number {
        return weeklyHousingStipend / 7;
    }

    calculateDailyMealsAmountWithDailyHousingStipend(dailyHousingStipend: number): number {
        return dailyHousingStipend * (4 / 6);
    }

    calculateMealsDailyAmountFromWeeklyMealsStipend(weeklyMealsStipend: number): number {
        return weeklyMealsStipend / 7;
    }

    calculateDailyHousingStipendWithDailyMealsStipend(dailyMealsStipend: number): number {
        return dailyMealsStipend * (6 / 4);
    }

    calculateStipendWithMealsDifference(dailyStipend: number, mealsStipendDifference: number): number {
        if (mealsStipendDifference <= 0) {
            return Math.round(dailyStipend * 7 * 100) / 100;
        } else {
            return Math.round((dailyStipend * 7 + mealsStipendDifference) * 100) / 100;
        }
    }

    calculateNonStandardBillingHours(
        shiftsPerWeek: number,
        shiftLength: number,
        altShiftsPerWeek: number,
        altShiftLength: number,
        from: Date,
        to: Date,
        vacationDates: Date[],
        totalOrientationHours: number,
        otHours: number,
        altOtHours: number
    ): number {
        // Use same logic for rotating and alternating - MC-1160
        // Originally it just affected billing hours
        // But, when comparing to legacy MC numbers weren't matching
        let primaryBookingWeeks: number;
        primaryBookingWeeks = this.calculatePrimaryBookingWeeks(from, to, vacationDates);
        primaryBookingWeeks = primaryBookingWeeks === 0 ? 1 : primaryBookingWeeks;        

        const primaryBillingHours = this.calculateRegularBillingHour(
            shiftLength,
            shiftsPerWeek,
            primaryBookingWeeks,
            0,
            otHours
        );

        // Store the primary billing hours used for later calculations - MC-1280
        this._marginBillingService.primaryNonStandardBillingHours = primaryBillingHours;          
        const alternateBookingWeeks = this.calculateAlternatingBookingWeeks(from, to, vacationDates);
        const alternateBillingHours = this.calculateRegularBillingHour(
            altShiftLength,
            altShiftsPerWeek,
            alternateBookingWeeks,
            0,
            altOtHours
        );

        // Store the alternate billing hours used for later calculations - MC-1280
        this._marginBillingService.alternateNonStandardBillingHours = alternateBillingHours;

        const result = primaryBillingHours + alternateBillingHours - totalOrientationHours;
        return result;
    }

    calculateNonStandardOverTimeBillingHours(
        shiftsPerWeek: number,
        shiftLength: number,
        altShiftsPerWeek: number,
        altShiftLength: number,
        from: Date,
        to: Date,
        vacationDates: Date[],
        otWeekOrDayFirst: string,
        andOrFirst: string,
        ruleHoursFirst: number,
        otWeekOrDaySecond: string,
        ruleHoursSecond: number,
        ruleHourCountFirst: string,
        ruleHourCountSecond: string
    ): number {
        const primaryBookingWeeks = this.calculatePrimaryBookingWeeks(from, to, vacationDates);
        const alternateBookingWeeks = this.calculateAlternatingBookingWeeks(from, to, vacationDates);

        let primaryBillingHours = 0;
        let alternateBillingHours = 0;
        if (otWeekOrDayFirst === HoursFrequency.TWO_WEEK || otWeekOrDaySecond === HoursFrequency.TWO_WEEK) {
            const otTotals = this.calculateNonStandard2WeekOverTimeBillingHour(
                otWeekOrDayFirst,
                andOrFirst,
                ruleHoursFirst,
                ruleHoursSecond,
                shiftLength,
                shiftsPerWeek,
                primaryBookingWeeks + alternateBookingWeeks,
                ruleHourCountFirst,
                ruleHourCountSecond,
                altShiftLength,
                altShiftsPerWeek
            );
            primaryBillingHours = otTotals.standardOtTotal;
            this._marginBillingService.primaryNonStandardOvertimeBillingHours = primaryBillingHours;
            alternateBillingHours = otTotals.altOtTotal;
            this._marginBillingService.alternateNonStandardOvertimeBillingHours = alternateBillingHours;
        } else {
            primaryBillingHours = this.calculateOverTimeBillingHour(
                otWeekOrDayFirst,
                andOrFirst,
                ruleHoursFirst,
                otWeekOrDaySecond,
                ruleHoursSecond,
                shiftLength,
                shiftsPerWeek,
                primaryBookingWeeks,
                ruleHourCountFirst,
                ruleHourCountSecond
            );
            // Store the primary overtime billing hours used for later calculations - MC-1280
            this._marginBillingService.primaryNonStandardOvertimeBillingHours = primaryBillingHours;
            alternateBillingHours = this.calculateOverTimeBillingHour(
                otWeekOrDayFirst,
                andOrFirst,
                ruleHoursFirst,
                otWeekOrDaySecond,
                ruleHoursSecond,
                altShiftLength,
                altShiftsPerWeek,
                alternateBookingWeeks,
                ruleHourCountFirst,
                ruleHourCountSecond
            );
            // Store the alternate overtime billing hours used for later calculations - MC-1280
            this._marginBillingService.alternateNonStandardOvertimeBillingHours = alternateBillingHours;
        }
        return primaryBillingHours + alternateBillingHours;
    }

    calculateNonStandard2WeekOverTimeBillingHour(
        otWeekOrDayFirst: string,
        andOrFirst: string,
        ruleHoursFirst: number,
        ruleHoursSecond: number,
        shiftLength: number,
        shiftsPerWeek: number,
        weeks: number,
        ruleHourCountFirst: string,
        ruleHourCountSecond: string,
        altShiftLength: number,
        altShiftsPerWeek: number
    ): OvertimeTotals {
        if (!otWeekOrDayFirst) {
            return { standardOtTotal: 0, altOtTotal: 0 };
        }

        const totalShiftHours = shiftLength * shiftsPerWeek;
        const altTotalShiftHours = altShiftLength * altShiftsPerWeek;

        if (andOrFirst === AndOr.EMPTY) {
            if (totalShiftHours + altTotalShiftHours > ruleHoursFirst) {
                return this._calculate2WeekOverTimeBillingHourAndOr(
                    shiftsPerWeek,
                    24,
                    ruleHoursFirst,
                    weeks,
                    shiftLength,
                    ruleHourCountFirst,
                    altShiftsPerWeek,
                    altShiftLength
                );
            } else {
                return { standardOtTotal: 0, altOtTotal: 0 };
            }
        } else if (andOrFirst === AndOr.AND || andOrFirst === AndOr.OR) {
            const totalShiftHoursWithDailyRule =
                otWeekOrDayFirst === HoursFrequency.DAILY ? ruleHoursFirst : ruleHoursSecond;
            const totalShiftHoursWithWeeklyRule =
                otWeekOrDayFirst === HoursFrequency.TWO_WEEK ? ruleHoursFirst : ruleHoursSecond;
            const count = otWeekOrDayFirst === HoursFrequency.DAILY ? ruleHourCountSecond : ruleHourCountFirst;
            return this._calculate2WeekOverTimeBillingHourAndOr(
                shiftsPerWeek,
                totalShiftHoursWithDailyRule,
                totalShiftHoursWithWeeklyRule,
                weeks,
                shiftLength,
                count,
                altShiftsPerWeek,
                altShiftLength
            );
        }
        return { standardOtTotal: 0, altOtTotal: 0 };
    }

    roundNumber(value: number, roundToNextWholeNumber = false): number {
        return FormulaHelperService.roundNumber(value, roundToNextWholeNumber);
    }

    calculateWeeklyHousingFromHousingStipendPerShift(numOfShifts: number, housingStipendPerShift: number): number {
        return numOfShifts * housingStipendPerShift;
    }

    calculateWeeklyMealsFromMealsStipendPerShift(numOfShifts: number, mealsStipendPerShift: number): number {
        return numOfShifts * mealsStipendPerShift;
    }

    /**
     * Calculates the minimum premium pay rate
     * @param basePayRate base pay rate
     * @returns number rounded to the cent using the round half up rounding method.
     */
    calculateMinPremiumPayRate(basePayRate: number): number {
        return FormulaHelperService.calculateMinPremiumPayRate(basePayRate);
    }

    /**
     * Checks if the passed in number (which might shows as 0 when rounded to a user) is actually 0 or not. This is used to determine
     * if we need to show an indicator to the user which shows what an actual percentage is out to as many as 14 decimal places
     * @param value number to check if is "near" zero
     * @returns boolean
     */
    isNearZero(value: number): boolean {
        return value !== null && value !== 0 && new Big(value).round(4).toNumber() === 0;
    }
}
